import React, { useState, useRef, useEffect } from 'react';
import './VoiceRecorder.css';

const VoiceRecorder = ({ onVoiceMessage, isLoading }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [permissionDenied, setPermissionDenied] = useState(false);

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    const audioRef = useRef(null);

    useEffect(() => {
        checkMicrophonePermission();
    }, []);

    const checkMicrophonePermission = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach(track => track.stop()); 
            setPermissionDenied(false);
        } catch (error) {
            console.error('Microphone permission denied:', error);
            setPermissionDenied(true);
        }
    };

    const startRecording = async () => {
        try {
            setPermissionDenied(false);
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100
                }
            });

            mediaRecorderRef.current = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus'
            });

            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                setAudioBlob(audioBlob);
                setIsPreviewMode(true);

                // Stop all tracks
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
            setRecordingTime(0);

 
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => {
                    if (prev >= 300) { 
                        stopRecording();
                        return prev;
                    }
                    return prev + 1;
                });
            }, 1000);

        } catch (error) {
            console.error('Error starting recording:', error);
            setPermissionDenied(true);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);

            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        }
    };

    const cancelRecording = () => {
        if (isRecording) {
            stopRecording();
        }

        setAudioBlob(null);
        setIsPreviewMode(false);
        setRecordingTime(0);

        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const sendVoiceMessage = () => {
        if (audioBlob && onVoiceMessage) {
            onVoiceMessage(audioBlob);
            setAudioBlob(null);
            setIsPreviewMode(false);
            setRecordingTime(0);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const playPreview = () => {
        if (audioBlob && audioRef.current) {
            const audioUrl = URL.createObjectURL(audioBlob);
            audioRef.current.src = audioUrl;
            audioRef.current.play();
        }
    };

    if (permissionDenied) {
        return (
            <div className="voice-recorder permission-denied">
                <div className="permission-message">
                    <span>🎤</span>
                    <p>Microphone access is required for voice messages.</p>
                    <button onClick={checkMicrophonePermission} className="permission-btn">
                        Grant Permission
                    </button>
                </div>
            </div>
        );
    }

    if (isPreviewMode && audioBlob) {
        return (
            <div className="voice-recorder preview-mode">
                <div className="preview-controls">
                    <audio ref={audioRef} />
                    <button onClick={playPreview} className="play-btn">
                        ▶️ Play
                    </button>
                    <span className="recording-duration">
                        {formatTime(recordingTime)}
                    </span>
                </div>
                <div className="preview-actions">
                    <button
                        onClick={cancelRecording}
                        className="cancel-btn"
                        disabled={isLoading}
                    >
                        🗑️
                    </button>
                    <button
                        onClick={sendVoiceMessage}
                        className="send-voice-btn"
                        disabled={isLoading}
                    >
                        {isLoading ? '🔄' : '📤'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="voice-recorder">
            {isRecording ? (
                <div className="recording-active">
                    <div className="recording-indicator">
                        <div className="pulse-dot"></div>
                        <span>{formatTime(recordingTime)}</span>
                    </div>
                    <div className="recording-controls">
                        <button onClick={cancelRecording} className="cancel-btn">
                            ❌
                        </button>
                        <button onClick={stopRecording} className="stop-btn">
                            ⏹️
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    onClick={startRecording}
                    className="start-recording-btn"
                    disabled={isLoading}
                    title="Record voice message"
                >
                    🎤
                </button>
            )}
        </div>
    );
};

export default VoiceRecorder;